import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, State} from '@App/state'
import {bindActionCreators} from "@reduxjs/toolkit";
import {useServices} from "@App/hooks/api/useServices";
import {PremiumService, Service} from "@App/types";
import {apolloClient} from "@App/graphql/client";
import {
    GET_ORGANIZATION_PREMIUM_SERVICES,
    OrganizationPremiumServicesQuery
} from "@App/graphql/queries/getOrganizationPremiumServices";
import {GET_VEHICLETREE, VehicletreeQuery} from "@App/graphql/queries";
import {fuelTypeIsBevForAyvens} from "@App/utils/conditionals/combinations";
import {PremiumServiceTypeIsSubService} from "@App/utils/conditionals/services";

export const useDriverData = () => {
    const {services} = useServices()
    const organization = useSelector((state: State) => state.orders.organization)
    const driverResponse = useSelector((state: State) => state.auth.driverData)

    const [availablePremiumservices, setAvailablePremiumservices] = useState<PremiumService[] | any>([])
    const [availablePremiumservicesLoading, setAvailableavailablePremiumservicesLoading] = useState<boolean>(false)

    const dispatch = useDispatch();
    const {
        chooseService,
        addPremiumService,
        chooseBrand,
        changeBrandFilter,
        chooseGeneration,
        changeBevSupportFilter
    } = bindActionCreators(actionCreators, dispatch)

    const fetchVehicletree = (brandId: number, generationId?: number) => {
        apolloClient.query<VehicletreeQuery>({
            query: GET_VEHICLETREE,
            variables: {
                ...brandId && {brandId: +brandId}, // use + to convert string to number
                ...generationId && {generationId: +generationId}, // use + to convert string to number
                includeGenerations: false,
                includeMotorizations: false
            }
        }).then((result) => {
            chooseBrand(result.data.brands[0])
            changeBrandFilter(result.data.brands[0])
            if (generationId) {
                chooseGeneration(result.data.brands[0].generations[0])
            }
        })
    }

    const setVehicleDataFromDriverResponse = () => {
        fetchVehicletree(driverResponse.keyNumber.type.generation.brand.id, driverResponse.keyNumber.type.generation.id)
    }

    const setBevSupportFromDriverResponse = () => {
        if (fuelTypeIsBevForAyvens(driverResponse.keyNumber.type.fuelType.id)) {
            changeBevSupportFilter(true)
        }
    }

    const getPremiumServices = () => {
        setAvailableavailablePremiumservicesLoading(true)
       if(services?.length > 0) {
           apolloClient.query<OrganizationPremiumServicesQuery>({
               query: GET_ORGANIZATION_PREMIUM_SERVICES, variables:
                   {
                       organization: organization.id,
                       services: services.map((service: Service) => service.id),
                   },
           }).then((res) => {
               const premiumServices = res.data.organization.premium_services
               setAvailablePremiumservices(premiumServices)
               setAvailableavailablePremiumservicesLoading(false)
           })
       }
    }

    useEffect(() => {
        getPremiumServices()
    }, [services]);

    const createRequiredServiceArray = (serviceCollection) => {
        let requiredServices:Service[] = []
        if(serviceCollection?.length > 0) {
            serviceCollection.map((unsanitizedService: any) => {
                const service = services?.find((service: Service) => service.id === unsanitizedService.id.toString())
                if (service) {
                    requiredServices.push(service)
                    chooseService(service)
                }
            })
        }
        return requiredServices;
    }

    const createOptionalServiceArray = (serviceCollection) => {
        let optionalServices:Service[] = []
        if(serviceCollection?.length > 0) {
            serviceCollection.map((unsanitizedService: any) => {
                const service = services?.find((service: Service) => service.id === unsanitizedService.id.toString())
                if (service) {
                    optionalServices.push(service)
                }
            })
        }
        return optionalServices;
    }

    const createRequiredPremiumserviceArray = (serviceCollection) => {
        let requiredPremiumServices:PremiumService[] = []
        if(serviceCollection?.length > 0) {
            serviceCollection.map((unsanitizedService: any) => {
                const service = availablePremiumservices?.find((service: Service) => service.id === unsanitizedService.id.toString())
                if (service && PremiumServiceTypeIsSubService(service)) {
                    requiredPremiumServices.push(service)
                    addPremiumService(service)
                }
            })
        }
        return requiredPremiumServices;
    }

    const createOptionalPremiumserviceArray = (serviceCollection) => {
        let optionalPremiumServices:PremiumService[] = []
        if(serviceCollection?.length > 0) {
            serviceCollection.map((unsanitizedService: any) => {
                const service = availablePremiumservices?.find((service: Service) => service.id === unsanitizedService.id.toString())
                if (service && PremiumServiceTypeIsSubService(service)) {
                    optionalPremiumServices.push(service)
                }
            })
        }
        return optionalPremiumServices;
    }

    return {
        createRequiredServiceArray,
        createOptionalServiceArray,
        createRequiredPremiumserviceArray,
        createOptionalPremiumserviceArray,
        availablePremiumservices,
        availablePremiumservicesLoading,
        getPremiumServices,
        driverResponse,
        setVehicleDataFromDriverResponse,
        setBevSupportFromDriverResponse
    }
}
